import { Avatar, Box, Button, Flex, Heading, HStack, Icon, SimpleGrid, Text, useToast, VStack } from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  PiCameraDuotone as FiCamera,
  PiSealCheckDuotone as FiCheckCircle,
  PiDownloadSimpleDuotone as FiDownload,
  PiUploadSimpleDuotone as FiUpload,
  PiArrowSquareOutDuotone as FiExternalLink,
  PiUserDuotone as FiUser,
  PiFileTextDuotone as FiFileText
} from "react-icons/pi";
import type { IconType } from "react-icons";
import { drgba } from "../../../../components/glassUi";
import { bindProperty, FilePreview } from "@lubbezposrednio-frontend/core";
import { CustomFormControl, CustomInput } from "@lubbezposrednio-frontend/commons";
import { ProfileViewModel } from "../../ProfileViewModel";
import { ProfileButtonsSection } from "../ProfileButtonsSection/ProfileButtonsSection";
import { useImageDropzone } from "../../../../hooks/useImageDropzone";
import { usePdfDropzone } from "../../../../hooks/usePdfDropzone";
import ImageCropModal from "../../../../components/ImageCropModal";

const Card: React.FC<{ title?: string; icon?: IconType; children: React.ReactNode }> = ({ title, icon, children }) => (
  <Box
    borderRadius="18px"
    bg="rgba(24,12,48,0.028)"
    p={{ base: "18px", md: "22px" }}
  >
    {title && (
      <Flex align="center" gap="12px" mb="20px">
        {icon && (
          <Flex w="42px" h="42px" borderRadius="13px" align="center" justify="center" color="#7b2ff7" flexShrink={0} bg={drgba("#7b2ff7", 0.12)}>
            <Icon as={icon} boxSize="20px" />
          </Flex>
        )}
        <Heading fontSize="17px" fontWeight="800" color="#241536" letterSpacing="-0.01em">
          {title}
        </Heading>
      </Flex>
    )}
    {children}
  </Box>
);

interface UserProfileProps {
  model: ProfileViewModel;
  isOrganization?: boolean;
}

export const UserProfile: React.FC<UserProfileProps> = observer(({ model, isOrganization }) => {
  const toast = useToast();
  // Wybór pliku → cropper (kwadrat + re-enkodowanie/EXIF) → dopiero potem podgląd/zapis na modelu.
  const [cropFile, setCropFile] = useState<File | null>(null);
  const image = useImageDropzone(f => setCropFile(f), toast);
  const onCropped = (f: File) => model.setUserProfilePhoto(Object.assign(f, { src: URL.createObjectURL(f) }) as FilePreview);
  const pdf = usePdfDropzone(model.setProfileStatement, toast);
  const fullName = `${model.firstName} ${model.lastName}`.trim();

  return (
    <VStack align="stretch" spacing="16px" w="100%">
      {/* Nagłówek profilu */}
      <Card>
        <Flex align="center" gap="20px" wrap="wrap">
          <Box position="relative" flexShrink={0}>
            <input {...image.getInputProps()} />
            <ImageCropModal file={cropFile} isOpen={!!cropFile} onClose={() => setCropFile(null)} onCropped={onCropped} title="Zdjęcie profilowe" />
            <Avatar size="xl" name={fullName || "?"} src={model.userPhotoPreview ?? undefined} bg="primary" color="white" />
            <Flex as="button" type="button" onClick={image.open} aria-label="Zmień zdjęcie" position="absolute" bottom="-2px" right="-2px" w="34px" h="34px" borderRadius="full" bg="accent" color="white" align="center" justify="center" border="3px solid white" _hover={{ bg: "#c41a3a" }}>
              <Icon as={FiCamera} boxSize="15px" />
            </Flex>
          </Box>
          <Box flex="1" minW={0}>
            <HStack spacing="9px" mb="2px" wrap="wrap">
              <Heading fontSize={{ base: "20px", md: "23px" }} fontWeight="800" color="primary" noOfLines={1}>
                {fullName || "Twoje konto"}
              </Heading>
              {model.isUserVerified && (
                <HStack spacing="5px" bg="rgba(47,206,37,0.12)" color="#1f9e18" px="9px" py="3px" borderRadius="full">
                  <Icon as={FiCheckCircle} boxSize="13px" />
                  <Text fontSize="12px" fontWeight="700">
                    Zweryfikowany
                  </Text>
                </HStack>
              )}
            </HStack>
            <Text fontSize="14px" color="gray.500" noOfLines={1}>
              {model.email}
            </Text>
            <Button onClick={image.open} size="sm" mt="12px" variant="outline" borderColor="primary" color="primary" borderRadius="10px" fontWeight="700" leftIcon={<FiCamera />} _hover={{ bg: "rgba(67,0,152,0.05)" }}>
              Zmień zdjęcie
            </Button>
          </Box>
        </Flex>
      </Card>

      {/* Dane */}
      <Card title="Dane osobowe" icon={FiUser}>
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing="16px">
          <CustomFormControl labelTitle={isOrganization ? "Imię przedstawiciela" : "Imię"}>
            <CustomInput autoComplete="given-name" {...bindProperty(model, "firstName")} variant="profile" />
          </CustomFormControl>
          <CustomFormControl labelTitle={isOrganization ? "Nazwisko przedstawiciela" : "Nazwisko"}>
            <CustomInput autoComplete="family-name" {...bindProperty(model, "lastName")} variant="profile" />
          </CustomFormControl>
          <CustomFormControl labelTitle="E-mail">
            <CustomInput autoComplete="email" disabled {...bindProperty(model, "email")} variant="profile" />
          </CustomFormControl>
          <CustomFormControl labelTitle="Nr telefonu">
            <CustomInput autoComplete="tel" {...bindProperty(model, "phoneNumber")} variant="profile" />
          </CustomFormControl>
          <CustomFormControl labelTitle="Kod pocztowy">
            <CustomInput autoComplete="postal-code" {...bindProperty(model, "postCode")} variant="profile" />
          </CustomFormControl>
        </SimpleGrid>
      </Card>

      {/* Oświadczenie */}
      <Card title="Oświadczenie" icon={FiFileText}>
        <Text fontSize="14px" color="gray.500" mb="16px">
          Pobierz wzór, podpisz i wgraj — potrzebne do weryfikacji organizatora.
        </Text>
        <input {...pdf.getInputProps()} />
        <Flex gap="12px" wrap="wrap">
          <Button onClick={model.downloadAffirmationTemplate} variant="outline" borderColor="primary" color="primary" borderRadius="12px" fontWeight="700" leftIcon={<FiDownload />} _hover={{ bg: "rgba(67,0,152,0.05)" }}>
            Pobierz wzór
          </Button>
          <Button onClick={pdf.open} bg="primary" color="white" borderRadius="12px" fontWeight="700" leftIcon={<FiUpload />} _hover={{ bg: "#36007a" }}>
            Wgraj podpisane
          </Button>
          {model.statementUrl && (
            <Button as={Link} to={model.statementUrl} target="_blank" variant="ghost" color="gray.500" borderRadius="12px" fontWeight="700" leftIcon={<FiExternalLink />} _hover={{ color: "primary", bg: "rgba(67,0,152,0.04)" }}>
              Otwórz wgrane
            </Button>
          )}
        </Flex>
      </Card>

      <ProfileButtonsSection model={model} />
    </VStack>
  );
});
